import { goalMetricLabels } from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { PlanHealthBanner } from "components/marketing-plan/plan-health-banner";
import { PlanModal } from "components/marketing-plan/plan-modal";
import { usePlanHealth } from "components/marketing-plan/use-plan-health";
import { trpc } from "components/rpc/trpc";
import { Spinner } from "components/ui/spinner";
import React, { useState } from "react";
import { useFeature, useNow } from "store/selectors/hooks";
import {
    DisplayMetric,
    displayMetricBarColor,
    displayMetrics,
    displayMetricValue,
} from "./display-metrics";
import { MetricCard } from "./metric-card";
import { MetricProgressTable } from "./metric-progress-table";
import { currentYearMonth, monthElapsedFraction, ymAddMonths } from "./pace";
import { RecentActivityList } from "./recent-activity-list";
import { trackedMetrics as metricProgressMetrics } from "./use-metric-progress";

//making separate objects here for display metric labels, etc. because "Other Touchpoints" is calls, texts, and emails.  this leaves flexibility to re-use the underlying call/text/email values if needed.
const displayMetricLabels: Record<DisplayMetric, string> = {
    visit: goalMetricLabels.visit,
    otherTouchpoints: "Other Touchpoints",
    referrals: goalMetricLabels.referrals,
    careStarts: goalMetricLabels.careStarts,
};

export const MyGoalsContent: React.FC = () => {
    const now = useNow();
    const trpcClient = trpc.useTRPC();
    const marketingPlaybookEnabled = useFeature("marketingPlaybook");
    const month = currentYearMonth(now);
    const priorMonth = ymAddMonths(month, -1);
    const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
    const planHealth = usePlanHealth();

    const currentQuery = useQuery({
        ...trpcClient.marketingGoal.myAttainment.queryOptions({ month }),
    });
    const priorQuery = useQuery({
        ...trpcClient.marketingGoal.myAttainment.queryOptions({ month: priorMonth }),
    });
    const recentQuery = useQuery({
        ...trpcClient.marketingGoal.myRecentActivity.queryOptions({ month }),
    });

    const attainment = currentQuery.data;
    const prior = priorQuery.data;
    const frac = monthElapsedFraction(month, now);
    const goal = attainment?.goal ?? null;
    const trackedMetrics: DisplayMetric[] = goal
        ? displayMetrics.filter(metric => displayMetricValue(goal, metric) > 0)
        : [];

    return (
        <div className="space-y-4">
            {currentQuery.isLoading ? (
                <div className="flex justify-center rounded-lg bg-white p-12 ring-1 ring-gray-200">
                    <Spinner />
                </div>
            ) : currentQuery.isError ? (
                <div className="rounded-xl bg-white p-8 text-center text-sm text-red-600 ring-1 ring-gray-200">
                    We couldn&apos;t load your goals. Please try again.
                </div>
            ) : !goal || trackedMetrics.length === 0 ? (
                marketingPlaybookEnabled ? (
                    <PlanHealthBanner
                        planHealth={planHealth}
                        onViewPlan={() => setIsPlanModalOpen(true)}
                        noPlanMessage="You haven't set up any touchpoints in your marketing plan yet."
                    />
                ) : (
                    <div className="rounded-xl bg-white p-8 text-center text-sm text-gray-600 ring-1 ring-gray-200">
                        No marketing goals have been set for you yet.
                    </div>
                )
            ) : (
                <>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {trackedMetrics.map(metric => (
                            <MetricCard
                                key={metric}
                                label={displayMetricLabels[metric]}
                                barColor={displayMetricBarColor[metric]}
                                actual={displayMetricValue(attainment.actuals, metric)}
                                target={displayMetricValue(goal, metric)}
                                frac={frac}
                                footer={
                                    <>
                                        Prior month:{" "}
                                        <span className="font-semibold tabular-nums text-gray-700">
                                            {prior
                                                ? displayMetricValue(
                                                      prior.actuals,
                                                      metric,
                                                  )
                                                : 0}
                                        </span>
                                    </>
                                }
                            />
                        ))}
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="h-6 w-1 flex-shrink-0 rounded-full bg-brand-500" />
                        <h2 className="twoverride text-lg font-semibold text-gray-800">
                            Touchpoint Progress
                        </h2>
                    </div>
                    <PlanHealthBanner
                        planHealth={planHealth}
                        onViewPlan={() => setIsPlanModalOpen(true)}
                        noPlanMessage="You haven't set up any touchpoints in your marketing plan yet."
                    />
                    <div className="space-y-3 rounded-xl bg-white px-3 py-4 ring-1 ring-gray-200 sm:px-4">
                        <MetricProgressTable
                            metrics={metricProgressMetrics}
                            density="comfortable"
                        />
                    </div>
                    <RecentActivityList recent={recentQuery.data ?? []} />
                </>
            )}
            <PlanModal
                isOpen={isPlanModalOpen}
                onDismiss={() => setIsPlanModalOpen(false)}
            />
        </div>
    );
};
